import { DEFAULT_INTERFACE_LANGUAGE } from '@/constants/interfaceLanguages'
import { DEFAULT_PARTNER } from '@/constants/partners'
import type { MarketProgram } from '@/features/site/types/marketProgram'
import type {
  CreateSite,
  CreateSiteForm,
  NotificationPreferences,
  Site,
  SiteMarketProgramContractRequest,
  UpdateSiteNotificationPreferencesForm,
} from '@/features/site/types/site'
import { formatIban } from '@/utils/iban'

/**
 * This function is meant to use when sending the form data to the backend. It just adjust the form
 * data to the backend format.
 */
export function convertCreateSiteFormToSite({
  financialInformation,
  notificationPreferences,
  marketProgramContracts,
  ...formData
}: CreateSiteForm): CreateSite {
  return {
    ...formData,
    countryCode: formData.countryCode as CountryIdentifier,
    marketProgramContracts: convertMarketProgramContractsFormToModel(marketProgramContracts),
    financialInformation: financialInformation
      ? {
          ...financialInformation,
          iban: financialInformation.iban ? formatIban(financialInformation.iban) : null,
        }
      : undefined,
    notificationPreferences: notificationPreferences
      ? convertNotificationPreferencesFormToNotificationPreferences({ notificationPreferences })
      : undefined,
  }
}

export function convertMarketProgramContractsFormToModel(marketProgramContracts): SiteMarketProgramContractRequest[] {
  return (
    marketProgramContracts
      ?.filter((marketProgramContract) => marketProgramContract.isSelected)
      .map(
        (marketProgramContract): SiteMarketProgramContractRequest => ({
          marketProgramId: marketProgramContract.marketProgramId,
          revenueSharingFraction: marketProgramContract.revenueSharingFraction,
          roundingErrorInWatts:
            marketProgramContract.roundingErrorInKilowatts !== null
              ? marketProgramContract.roundingErrorInKilowatts * 1000
              : null,
          soldCapacityAlgorithmType:
            marketProgramContract.soldCapacityAlgorithmType !== ''
              ? marketProgramContract.soldCapacityAlgorithmType
              : null,
        }),
      ) ?? []
  )
}

/**
 * This function is meant to use receiving the data from the backend. It just adjust the backend data to the create
 * site form format.
 *
 * When site is passed (e.g on the creating form), then we return some default values for the form.
 */
export function convertSiteToCreateSiteForm(site?: Site): CreateSiteForm {
  return {
    ...site,
    name: '',
    partnerCode: DEFAULT_PARTNER.partnerCode,
    partnerName: DEFAULT_PARTNER.name,
    symbolicName: '',
    location: '',
    address: '',
    timezone: '',
    contacts: [],
    notificationPreferences: convertNotificationPreferencesToNotificationPreferencesForm().notificationPreferences,
    financialInformation: {
      iban: null,
      ibanHolder: null,
    },
    marketProgramContracts:
      site?.marketProgramContracts?.map((marketProgramContract) => ({
        marketProgramId: marketProgramContract.marketProgram!.id,
        revenueSharingFraction: marketProgramContract.revenueSharingFraction!,
        roundingErrorInKilowatts: marketProgramContract.roundingErrorInWatts
          ? marketProgramContract.roundingErrorInWatts / 1000
          : null,
        soldCapacityAlgorithmType: marketProgramContract.soldCapacityAlgorithmType!,
        isSelected: true,
      })) ?? [],
  }
}

/**
 * On the market programs step, we are showing all the market programs available on a specific country. This function
 * adds them to the form data using some default data.
 *
 * Besides that, when a site already has data related to a market program, we just inject them.
 *
 * It is also important to understand that the market programs shown on the form are being rendered in the same order as
 * the market programs come from the backend. That's another reason we need siteMarketPrograms argument on this function:
 * we need to inject the data already on the form on the right market program index.
 */
export function convertMarketProgramsToFormMarketPrograms(
  marketPrograms: MarketProgram[],
  marketProgramContracts: CreateSiteForm['marketProgramContracts'],
  countryCode?: CountryIdentifier,
): CreateSiteForm['marketProgramContracts'] {
  return marketPrograms.map((marketProgram) => {
    const initialSiteMarketProgram: CreateSiteForm['marketProgramContracts'][0] = {
      marketProgramId: marketProgram.id,
      revenueSharingFraction: null,
      roundingErrorInKilowatts: countryCode?.toLocaleLowerCase() === 'fi' ? 15 : null,
      soldCapacityAlgorithmType: countryCode?.toLocaleLowerCase() === 'fi' ? 'min' : null,
      isSelected: false,
    }

    if (!marketProgramContracts) {
      return initialSiteMarketProgram
    }

    const siteMarketProgram = marketProgramContracts?.find(
      (siteMarketProgram) => siteMarketProgram?.marketProgramId === marketProgram?.id,
    )

    return siteMarketProgram ?? initialSiteMarketProgram
  })
}

/**
 * Phone numbers must contain at least one item. This is neccessary because the form always has one input field on the
 * phone numbers list.
 *
 * When this input field renders the first time, it registers with a value undefined, which causes the form to be invalid. That's why
 * we force the phone numbers to have at least one item with an empty string.
 */
export function convertNotificationPreferencesToNotificationPreferencesForm(
  notificationPreferences?: NotificationPreferences,
): UpdateSiteNotificationPreferencesForm {
  if (!notificationPreferences) {
    return {
      notificationPreferences: {
        phoneNumbers: [''],
        email: null,
        isSmsEnabled: false,
        isEmailEnabled: false,
        language: DEFAULT_INTERFACE_LANGUAGE.value,
      },
    }
  }

  const { phoneNumbers, ...props } = notificationPreferences
  const validPhoneNumbers = phoneNumbers?.filter((phoneNumber) => phoneNumber.length > 0) ?? []

  return {
    notificationPreferences: {
      phoneNumbers: validPhoneNumbers.length > 0 ? validPhoneNumbers : [''],
      ...props,
    },
  }
}

export function convertNotificationPreferencesFormToNotificationPreferences(
  formData?: UpdateSiteNotificationPreferencesForm,
): NotificationPreferences | undefined {
  const notificationPreferences = formData?.notificationPreferences ?? null

  return notificationPreferences
    ? {
        ...notificationPreferences,
        phoneNumbers: notificationPreferences.phoneNumbers?.filter((phoneNumber) => phoneNumber.length > 0) ?? [],
      }
    : undefined
}
